import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import SummaryCard from '@acorn-portal/shared/src/components/card/SummaryCard';
import AddressDisplay from '@acorn-portal/shared/src/components/display/address';
import DemandsNeeds from '@acorn-portal/shared/src/components/display/demands-needs';
import DriverExcess from '@acorn-portal/shared/src/components/display/driver-excess';
import Endorsements from '@acorn-portal/shared/src/components/display/endorsements';
import Price from '@acorn-portal/shared/src/components/display/price';
import UsageAndMilage from '@acorn-portal/shared/src/components/display/usage-and-milage';
import Vehicle from '@acorn-portal/shared/src/components/display/vehicle';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ErrorPage from '@acorn-portal/shared/src/pages/misc/ErrorPage';
import { Address, DriverExcess as DriverExcessAPI } from '@acorn-portal/shared/src/services/api';
import { CoverLevel, Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { PcVehicle } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import { setDriverData } from '@acorn-portal/shared/src/store/subscription-amend-drivers.slice';
import { setSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { resetSubscriptionChanges } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { formatCurrency, formatPounds } from '@acorn-portal/shared/src/utils';
import { DISPLAY_DATE_TIME_FULL_MONTH } from '@acorn-portal/shared/src/utils/constants';
import { VOLUNTARY_EXCESS_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, Grid, Typography, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { Form, Formik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import SubscriptionReferral from '../referral';
import { summarySchema } from '../validation/summary-schema';
import AutoRenewSubscription from './components/auto-renew-subscription';
import SendDocumentsByPost from './components/documents-by-post';
import Drivers from './components/drivers';
import { useVoluntaryExcessChange } from './hooks/use-voluntary-excess-change';
import { FormValues, initialFormValues } from './utils/constants';
import { getCompulsoryExcess } from './utils/get-compulsory-excess';
import { getEndDate } from './utils/get-end-date';
import { mapPcDriverToDriverDetails } from './utils/map-driver-to-driver-details';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const Summary: FC = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const { policyToken } = useParams();
	const { quoteData, effectiveDate: storedEffectiveDate } = useAppSelector((state) => state.subscription);
	const { handleVoluntaryExcessChange, isUpdatingExcess } = useVoluntaryExcessChange();
	const { getReferenceDataValue } = useReferenceData();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();
	const effectiveDate = storedEffectiveDate || searchParams.get('effectiveDate');
	const lists = useAppSelector((redux) => redux.referenceData);
	const subscriptionChange = useAppSelector((redux) => redux.subscriptionChangesReducer);
	const currentVoluntaryExcess = quoteData?.quoteDetails?.riskData?.coverDetails?.voluntaryExcess?.toString() || '';
	const currentLevelOfCover = quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover || '';
	const currentDriverDetails = quoteData?.quoteDetails?.riskData?.driverDetails || [];
	const isQuoteDataOrEffectiveDateMissing = !quoteData || !effectiveDate;
	const isDeclinedPolicy = quoteData?.quoteResponse?.underwrittenOptions?.[0]?.status === 'Declined';
	const effectiveDateParsed = parseISO(effectiveDate ?? '');
	const address = quoteData?.quoteDetails.riskData.proposerDetails.address as Address;
	const driverExcesses = quoteData?.excessBreakdown?.driverExcesses ?? [];

	const initialValues = {
		...initialFormValues,
		voluntaryExcess: currentVoluntaryExcess,
		levelOfCover: currentLevelOfCover,
	};

	const isSubscriptionUnchanged =
		!subscriptionChange.changeOfAddress &&
		!subscriptionChange.changeOfVehicle &&
		!subscriptionChange.changeOfDriver &&
		!subscriptionChange.changeOfUsage &&
		!subscriptionChange.changeOfExcess;

	const handleSubmit = (values: FormValues) => {
		dispatch(
			setSubscriptionData({
				policyToken: policyToken ?? '',
				effectiveDate: effectiveDate ?? '',
				sendWelcomePackStatus: values.SendDocumentsByPost,
				autoRenew: !values.optOut,
				quoteData: quoteData,
			}),
		);

		navigate(ROUTE_PATHS.subscriptionRestartPaymentOptions);
	};

	useEffect(() => {
		if (quoteData && isSubscriptionUnchanged) {
			dispatch(
				setSubscriptionData({
					policyToken: policyToken ?? '',
					effectiveDate: effectiveDate ?? '',
					sendWelcomePackStatus: false,
					autoRenew: true,
					quoteData: quoteData,
				}),
			);

			dispatch(resetSubscriptionChanges());
		}
	}, [quoteData, dispatch, policyToken, effectiveDate, subscriptionChange]);

	useEffect(() => {
		// Resets the WIP driver data - useful for e.g. backwards navigation
		if (currentDriverDetails) dispatch(setDriverData(currentDriverDetails));
	}, [currentDriverDetails]);

	if (isDeclinedPolicy) {
		return <SubscriptionReferral />;
	}

	if (isQuoteDataOrEffectiveDateMissing) {
		return <ErrorPage />;
	}

	return (
		<>
			<LoadingModal open={isUpdatingExcess} />
			<JourneyCardLayout pageTitle='Restart Summary' loading={false}>
				<Formik
					enableReinitialize
					initialValues={initialValues}
					onSubmit={handleSubmit}
					validationSchema={summarySchema}
				>
					{(formik) => {
						return (
							<Form>
								<FormikLogErrors />
								<Grid
									container
									spacing={2}
									alignItems='stretch'
									maxWidth='md'
									sx={(theme) => ({
										[theme.breakpoints.up('md')]: {
											mx: 'auto',
										},
									})}
								>
									<Grid size={{ xs: 12 }}>
										<Typography sx={{ fontSize: '14px' }}>
											Please review your renewal quote details below. If you are happy with your details, please click
											Continue. <b>Please note, any changes made will apply to your renewal policy.</b> If you wish to
											amend your renewal, or if you have updates regarding motoring claims or convictions, please
											contact one of our agents on{' '}
											<a
												style={{
													color: theme.palette.primary.main,
													cursor: 'pointer',
													fontWeight: 'bold',
												}}
												href='tel:01704339000'
											>
												01704 339 000
											</a>
											, who will be pleased to assist you.
										</Typography>
									</Grid>
									{/* Policy Period */}
									<Grid size={{ xs: 12, md: 6 }}>
										<Box
											sx={{
												border: '1px solid',
												borderColor: 'grey.300',
												p: 2,
												display: 'flex',
												flex: 1,
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'space-evenly',
												borderRadius: 2,
												height: '100%',
												width: '100%',
											}}
										>
											<Typography sx={{ fontSize: '1rem', mb: 1 }}>Your policy covers you from</Typography>
											<Box>
												<Typography variant='h6'>
													{format(effectiveDateParsed, DISPLAY_DATE_TIME_FULL_MONTH)}
												</Typography>
											</Box>
											<Divider variant='middle' sx={{ borderColor: 'black', width: '100%' }}>
												TO
											</Divider>
											<Box>
												<Typography variant='h6'>{getEndDate(effectiveDateParsed)}</Typography>
											</Box>
										</Box>
									</Grid>

									{/* Pricing Details */}
									<Grid size={{ xs: 12, md: 6 }}>
										<Box
											sx={{
												border: 1,
												borderColor: 'grey.300',
												p: 2,
												borderRadius: 2,
												width: '100%',
												height: '100%',
												display: 'flex',
												flexDirection: 'column',
												justifyContent: 'space-between',
												textAlign: 'right',
											}}
										>
											<Price
												color='primary.main'
												value={
													quoteData?.quoteResponse?.underwrittenOptions?.[0]?.paymentCollectionOptions?.[0]
														?.totalAmountPayable as number
												}
												description='Amount Payable'
											/>
											<Divider sx={{ my: 1 }} />
											<Typography sx={{ fontSize: '14px' }}>
												Prices include Insurance Premium Tax of{' '}
												<strong>{quoteData?.quoteResponse?.underwrittenOptions?.[0]?.price?.iptAmount ?? 0}%</strong>{' '}
												and a non-refundable Policy Administration fee of{' '}
												<strong>
													{formatCurrency(
														quoteData?.quoteResponse?.underwrittenOptions?.[0]?.price?.policyAdministrationFee ?? 0,
													)}
												</strong>
											</Typography>
										</Box>
									</Grid>

									{/* Vehicle Details */}
									<Grid size={{ xs: 12 }}>
										<SummaryCard
											title='Vehicle'
											isUpdated={subscriptionChange.changeOfVehicle}
											amendLink={ROUTE_PATHS.subscriptionRestartAmendVehicle1}
										>
											<Vehicle
												isSummaryScreen
												product={Product.PRIVATE_CAR}
												vehicle={{
													...(quoteData?.quoteDetails?.riskData?.vehicleDetails as PcVehicle),
													registrationNumber: String(quoteData?.quoteDetails?.riskData?.vehicleDetails?.registration),
													vehicleUse: String(quoteData?.quoteDetails?.riskData?.vehicleDetails?.vehicleUse),
												}}
											>
												<Typography>
													Estimated value: {formatPounds(quoteData?.quoteDetails?.riskData?.vehicleDetails?.value ?? 0)}
												</Typography>
												<Typography sx={{ display: 'flex', alignItems: 'start' }} data-testid='level-of-cover'>
													{getReferenceDataValue(
														ListNames.levelOfCoverTypes,
														quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover ?? '',
													)}
												</Typography>
											</Vehicle>
										</SummaryCard>
									</Grid>

									{/* Address */}
									<Grid size={{ xs: 12 }}>
										<SummaryCard
											title='Address'
											isUpdated={subscriptionChange.changeOfAddress}
											amendLink={ROUTE_PATHS.subscriptionRestartAmendAddress}
										>
											<AddressDisplay
												address={address}
												overnightAddress={quoteData?.quoteDetails?.riskData?.vehicleDetails?.overnightAddress}
											/>
										</SummaryCard>
									</Grid>

									{/* Drivers */}
									<Grid size={{ xs: 12 }}>
										<SummaryCard
											title='Drivers'
											isUpdated={subscriptionChange.changeOfDriver}
											amendLink={ROUTE_PATHS.subscriptionRestartAmendDrivers}
										>
											<Drivers drivers={quoteData?.quoteDetails?.riskData?.driverDetails || []} />
										</SummaryCard>
									</Grid>

									{/* Usage and Mileage */}
									<Grid size={{ xs: 12 }}>
										<SummaryCard
											title='Usage and Mileage'
											isUpdated={subscriptionChange.changeOfUsage}
											amendLink={ROUTE_PATHS.subscriptionRestartAmendUsage}
										>
											<UsageAndMilage
												vehicleUse={quoteData?.quoteDetails?.riskData?.vehicleDetails?.vehicleUse ?? ''}
												vehicleCover={quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover ?? ''}
												product={Product.PRIVATE_CAR}
												annualMileage={quoteData?.quoteDetails?.riskData?.vehicleDetails?.annualMileage ?? 0}
											/>
										</SummaryCard>
									</Grid>

									{/* Voluntary Excess */}

									<Grid size={{ xs: 12 }}>
										{quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover !== CoverLevel.TPO && (
											<SummaryCard
												title='Voluntary Excess (£)'
												showAmendButton={false}
												isUpdated={subscriptionChange.changeOfExcess}
											>
												<SelectFormField
													fillWidth
													label='Voluntary Excess (£)'
													labelVariant='desktopHidden'
													name='voluntaryExcess'
													id='voluntaryExcess'
													data-testid='voluntary-excess'
													value={Number(quoteData?.quoteDetails.riskData.coverDetails.voluntaryExcess ?? 0)}
													onChange={async (value: string) => {
														if (typeof value === 'number' || typeof value === 'string') {
															await handleVoluntaryExcessChange(Number(value));
															formik.setFieldValue('confirm', false);
														}
													}}
													options={lists.voluntaryExcessOptions as IOptions[]}
													isDisabled={isUpdatingExcess}
													modal={{
														title: VOLUNTARY_EXCESS_MODAL.header,
														description: VOLUNTARY_EXCESS_MODAL.text,
													}}
												/>
											</SummaryCard>
										)}
									</Grid>

									{/* Price Breakdown */}
									<Grid size={{ xs: 12 }}>
										<Box sx={{ width: 'inherit', pl: 2 }}>
											{quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover !== CoverLevel.TPO && (
												<>
													{quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover !== CoverLevel.TPO && (
														<Box sx={{ width: '100%', py: 2 }}>
															<Typography fontWeight='bold'>Policy Excess</Typography>
															<Grid container alignItems='center' sx={{ py: 2, mb: 1 }} gap={2}>
																<Grid flex={1}>
																	<Typography data-testid='voluntary-excess'>
																		<Price
																			value={quoteData?.quoteDetails?.riskData?.coverDetails?.voluntaryExcess ?? 0}
																			description='Voluntary Excess'
																		/>
																	</Typography>
																</Grid>
																<Grid>
																	<Price
																		value={getCompulsoryExcess(quoteData?.excessBreakdown?.compulsoryExcesses)}
																		description='Compulsory Excess'
																	/>
																</Grid>
															</Grid>
															<Divider />
														</Box>
													)}

													{quoteData?.quoteDetails.riskData?.driverDetails && (
														<DriverExcess
															drivers={(quoteData.quoteDetails.riskData.driverDetails || []).map(
																mapPcDriverToDriverDetails,
															)}
															driverExcesses={driverExcesses as DriverExcessAPI[]}
														/>
													)}
												</>
											)}
										</Box>
									</Grid>
									{/* Demands and Needs */}
									<Grid size={{ xs: 12 }}>
										<DemandsNeeds
											coverLevel={quoteData?.quoteDetails?.riskData?.coverDetails?.levelOfCover as CoverLevel}
											product={Product.PRIVATE_CAR}
										/>
									</Grid>

									{/* Endorsements */}
									<Grid size={{ xs: 12 }}>
										<Endorsements
											endorsements={quoteData?.quoteResponse?.underwrittenOptions?.[0]?.underwrittenEndorsements || []}
										/>
										<Divider />
									</Grid>

									{/* Auto Renewal and Send Documents By Post Options */}
									<Grid size={{ xs: 12 }}>
										<SendDocumentsByPost
											onChange={async (checked: boolean) => {
												formik.setFieldValue('SendDocumentsByPost', checked);
											}}
										/>
										<Divider sx={{ my: 2 }} />
										<AutoRenewSubscription
											onChange={async (checked: boolean) => {
												formik.setFieldValue('optOut', checked);
											}}
										/>
										<Divider sx={{ mt: 2 }} />
									</Grid>

									{/* Confirmation */}
									<Grid size={{ xs: 12 }}>
										<Box sx={{ width: 'inherit', pl: 2, mt: 4 }}>
											<FormControl error={formik.touched.confirm && Boolean(formik.errors.confirm)}>
												<FormControlLabel
													control={
														<Checkbox
															id='confirm'
															name='confirm'
															slotProps={{
																input: {
																	'aria-label': 'confirm',
																},
															}}
															icon={<CircleOutlined />}
															checkedIcon={<CheckCircle />}
															sx={{ '& .MuiSvgIcon-root': { fontSize: '3rem' } }}
															onChange={(e) => formik.setFieldValue('confirm', e.target.checked)}
															checked={formik.values.confirm}
														/>
													}
													labelPlacement='start'
													label='I confirm all the details above are correct.'
													sx={{ margin: 0 }}
												/>
											</FormControl>
										</Box>
									</Grid>
									{/* Action Buttons */}
									<Grid size={{ xs: 12 }}>
										<Box sx={{ width: 'inherit', pl: 2 }}>
											<ActionButtons>
												<BackButton onClick={() => setOpen(true)}>Back</BackButton>
												<ContinueButton type='submit' disabled={!formik.isValid || !formik.dirty}>
													Continue
												</ContinueButton>
											</ActionButtons>
											<HelperModal
												open={open}
												onClose={() => setOpen(false)}
												title='Discard The Changes?'
												description='Are you sure you wish to discard the changes?'
												closeText='No'
												submitText='Yes'
												onSubmit={() => {
													setOpen(false);
													navigate(`${ROUTE_PATHS.subscriptionRestart}/${policyToken}`);
												}}
											/>
										</Box>
									</Grid>
								</Grid>
							</Form>
						);
					}}
				</Formik>
			</JourneyCardLayout>
		</>
	);
};

export default Summary;
